import { useState, useEffect } from 'react'
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Switch } from '@/components/ui/switch'
import { Loader2, Scissors, CheckCircle2 } from 'lucide-react'
import { useVideoCut } from '@/api/hooks/useVideoCut'
import { toast } from 'sonner'
import type { MediaImage } from '@/types'

interface VideoCutDialogProps {
  video: MediaImage | null
  folderId: string
  folderName: string
  onClose: () => void
}

export function VideoCutDialog({ video, folderId, folderName, onClose }: VideoCutDialogProps) {
  const [clipTitle, setClipTitle] = useState('')
  const [maxClips, setMaxClips] = useState(5)
  const [reelsMode, setReelsMode] = useState(true)
  const [maxDuration, setMaxDuration] = useState(90)
  const videoCut = useVideoCut()
  const [result, setResult] = useState<{ clips: number; totalDuration: number; folderName: string } | null>(null)

  // Set default title from video filename
  useEffect(() => {
    if (video) {
      const name = video.filename.replace(/\.[^.]+$/, '')
      setClipTitle(name)
      setResult(null)
    }
  }, [video])

  const handleCut = () => {
    if (!video) return
    if (!clipTitle.trim()) { toast.error('Digite o titulo dos cortes'); return }
    setResult(null)
    videoCut.mutate({
      videoUrl: video.url,
      maxClips,
      maxDuration,
      folderId,
      clipTitle: clipTitle.trim(),
      reelsMode,
    }, {
      onSuccess: (data) => {
        setResult({ clips: data.clips.length, totalDuration: data.totalDuration, folderName: data.targetFolderName })
        toast.success(`${data.clips.length} corte(s) salvo(s) na pasta "${data.targetFolderName}"`)
      },
      onError: (err) => toast.error(err.message),
    })
  }

  const handleClose = () => {
    if (videoCut.isPending) return
    setResult(null)
    onClose()
  }

  return (
    <Dialog open={!!video} onOpenChange={(open) => { if (!open) handleClose() }}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-base">
            <Scissors className="h-4 w-4" /> Cortar Video
          </DialogTitle>
          <DialogDescription className="text-xs">
            Divida o video em clips menores para postar nas redes sociais
          </DialogDescription>
        </DialogHeader>

        {video && (
          <div className="space-y-4">
            {/* Video preview */}
            <div className="overflow-hidden rounded-md border border-border">
              <video
                src={video.url}
                controls
                className="aspect-video w-full bg-black"
              />
            </div>

            {/* Title input */}
            <div className="space-y-1.5">
              <label className="text-xs font-medium">Titulo dos cortes</label>
              <Input
                value={clipTitle}
                onChange={(e) => setClipTitle(e.target.value)}
                placeholder="Ex: Mendigo vai ao hotel"
                className="h-9 text-sm"
                disabled={videoCut.isPending}
              />
              <p className="text-[11px] text-muted-foreground">
                Resultado: "{clipTitle || '...'} - PARTE 1", "{clipTitle || '...'} - PARTE 2", etc
              </p>
            </div>

            {/* Reels mode toggle */}
            <div className="flex items-center justify-between rounded-lg border border-border p-2.5">
              <div>
                <p className="text-xs font-medium">Modo Reels</p>
                <p className="text-[11px] text-muted-foreground">FB: 3-90s | IG: 3-180s</p>
              </div>
              <Switch checked={reelsMode} onCheckedChange={(v) => {
                setReelsMode(v)
                if (v && maxDuration > 90) setMaxDuration(90)
              }} />
            </div>

            {/* Settings */}
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <label className="text-xs font-medium">Max cortes</label>
                <Input
                  type="number"
                  min={1}
                  max={20}
                  value={maxClips || ''}
                  onChange={(e) => setMaxClips(Math.min(20, parseInt(e.target.value) || 0))}
                  onBlur={() => setMaxClips(v => Math.max(1, v))}
                  className="h-9 text-sm"
                  disabled={videoCut.isPending}
                />
                <p className="text-[11px] text-muted-foreground">1 a 20 clips</p>
              </div>
              <div className="space-y-1.5">
                <label className="text-xs font-medium">Duracao max (seg)</label>
                <Input
                  type="number"
                  min={reelsMode ? 3 : 10}
                  max={reelsMode ? 90 : 600}
                  value={maxDuration || ''}
                  onChange={(e) => {
                    const max = reelsMode ? 90 : 600
                    setMaxDuration(Math.min(max, parseInt(e.target.value) || 0))
                  }}
                  onBlur={() => { const min = reelsMode ? 3 : 10; setMaxDuration(v => Math.max(min, v)) }}
                  className="h-9 text-sm"
                  disabled={videoCut.isPending}
                />
                <p className="text-[11px] text-muted-foreground">{reelsMode ? '3 a 90s (Reels)' : '10 a 600 segundos'}</p>
              </div>
            </div>

            {/* Loading state */}
            {videoCut.isPending && (
              <div className="flex items-center gap-3 rounded-lg border border-primary/20 bg-primary/5 p-4">
                <Loader2 className="h-5 w-5 animate-spin text-primary shrink-0" />
                <div>
                  <p className="text-sm font-medium text-primary">Cortando video...</p>
                  <p className="text-[11px] text-muted-foreground">Pode levar alguns minutos dependendo do tamanho</p>
                </div>
              </div>
            )}

            {/* Result */}
            {result && (
              <div className="flex items-center gap-3 rounded-lg border border-green-500/20 bg-green-500/5 p-4">
                <CheckCircle2 className="h-5 w-5 text-green-600 shrink-0" />
                <div>
                  <p className="text-sm font-medium text-green-700">
                    {result.clips} corte(s) salvo(s)!
                  </p>
                  <p className="text-[11px] text-muted-foreground">
                    Pasta criada: "{result.folderName}"
                  </p>
                  <p className="text-[11px] text-muted-foreground/70">
                    Video original: {Math.floor(result.totalDuration / 60)}min {result.totalDuration % 60}s
                  </p>
                </div>
              </div>
            )}

            {/* Actions */}
            <div className="flex justify-end gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={handleClose}
                disabled={videoCut.isPending}
              >
                {result ? 'Fechar' : 'Cancelar'}
              </Button>
              {!result && (
                <Button
                  size="sm"
                  className="gap-1.5"
                  onClick={handleCut}
                  disabled={videoCut.isPending || !clipTitle.trim()}
                >
                  <Scissors className="h-3.5 w-3.5" />
                  Cortar Video
                </Button>
              )}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  )
}
